// client/src/pages/admin/AdminAnalytics.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../services/api';
import axios from 'axios';
import { Users, Package, ShoppingCart, BarChart3, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

// ================================================================
// CHART COMPONENTS (pure SVG — no dependencies)
// ================================================================

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Bar Chart with gradient fills
const BarChart = ({ data, valueKey = 'count', color = '#6366f1', height = 200 }) => {
  if (!data || data.length === 0) return <div className="text-center text-gray-400 py-10 text-sm">No data available</div>;

  const values = data.map(d => d[valueKey] || 0);
  const maxVal = Math.max(...values, 1);
  const pad = { t: 25, r: 15, b: 35, l: 40 };
  const W = 500, H = height;
  const iW = W - pad.l - pad.r;
  const iH = H - pad.t - pad.b;
  const barW = Math.min((iW / data.length) * 0.55, 40);
  const gap = iW / data.length;
  const yTicks = Array.from({ length: 5 }, (_, i) => Math.round((maxVal / 4) * i));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: `${H}px` }}>
      <defs>
        <linearGradient id={`bg-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={color} stopOpacity="0.35" />
        </linearGradient>
      </defs>
      {/* Y grid + labels */}
      {yTicks.map((v, i) => {
        const y = pad.t + iH - (v / maxVal) * iH;
        return (
          <g key={i}>
            <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="#f0f0f0" strokeWidth="1" />
            <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#9ca3af">{v}</text>
          </g>
        );
      })}
      {/* Bars */}
      {data.map((d, i) => {
        const v = d[valueKey] || 0;
        const bH = (v / maxVal) * iH;
        const x = pad.l + i * gap + (gap - barW) / 2;
        const y = pad.t + iH - bH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bH} rx="4" fill={`url(#bg-${color.slice(1)})`} />
            <text x={x + barW / 2} y={y - 6} textAnchor="middle" fontSize="11" fontWeight="600" fill="#374151">
              {v}
            </text>
            <text x={x + barW / 2} y={H - pad.b + 18} textAnchor="middle" fontSize="11" fill="#6b7280">
              {MONTHS[(d._id?.month || 1) - 1]}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// Area Chart for revenue
const AreaChart = ({ data, color = '#10b981', height = 200 }) => {
  if (!data || data.length === 0) return <div className="text-center text-gray-400 py-10 text-sm">No data available</div>;

  const values = data.map(d => d.revenue || 0);
  const maxVal = Math.max(...values, 1);
  const pad = { t: 25, r: 15, b: 35, l: 55 };
  const W = 500, H = height;
  const iW = W - pad.l - pad.r;
  const iH = H - pad.t - pad.b;

  const pts = values.map((v, i) => ({
    x: pad.l + (i / Math.max(values.length - 1, 1)) * iW,
    y: pad.t + iH - (v / maxVal) * iH
  }));
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const area = `${line} L${pts[pts.length - 1].x},${pad.t + iH} L${pts[0].x},${pad.t + iH} Z`;
  const yTicks = Array.from({ length: 5 }, (_, i) => Math.round((maxVal / 4) * i));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: `${H}px` }}>
      <defs>
        <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {yTicks.map((v, i) => {
        const y = pad.t + iH - (v / maxVal) * iH;
        return (
          <g key={i}>
            <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="#f0f0f0" strokeWidth="1" />
            <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#9ca3af">€{v >= 1000 ? `${(v/1000).toFixed(1)}k` : v}</text>
          </g>
        );
      })}
      <path d={area} fill="url(#areaFill)" />
      <path d={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="5" fill="white" stroke={color} strokeWidth="2" />
          <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="11" fontWeight="600" fill="#374151">
            €{values[i] >= 1000 ? `${(values[i]/1000).toFixed(1)}k` : values[i].toFixed(0)}
          </text>
          <text x={p.x} y={H - pad.b + 18} textAnchor="middle" fontSize="11" fill="#6b7280">
            {MONTHS[(data[i]._id?.month || 1) - 1]}
          </text>
        </g>
      ))}
    </svg>
  );
};

// Donut Chart for order status
const DonutChart = ({ data }) => {
  const statusColors = {
    pending: '#f59e0b', confirmed: '#6366f1', processing: '#3b82f6',
    shipped: '#8b5cf6', delivered: '#10b981', cancelled: '#ef4444', in_progress: '#3b82f6'
  };
  const entries = Object.entries(data || {}).filter(([_, v]) => v > 0);
  const total = entries.reduce((s, [_, v]) => s + v, 0);
  if (total === 0) return <div className="text-center text-gray-400 py-6 text-sm">No orders</div>;

  const R = 60, CX = 80, CY = 80, C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="flex items-center justify-center gap-8 py-2">
      <svg width="160" height="160" viewBox="0 0 160 160">
        {entries.map(([status, count]) => {
          const dash = (count / total) * C;
          const o = offset;
          offset += dash;
          return (
            <circle key={status} cx={CX} cy={CY} r={R} fill="none"
              stroke={statusColors[status] || '#94a3b8'} strokeWidth="22"
              strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-o}
              transform={`rotate(-90 ${CX} ${CY})`} />
          );
        })}
        <text x={CX} y={CY - 5} textAnchor="middle" fontSize="28" fontWeight="700" fill="#111827">{total}</text>
        <text x={CX} y={CY + 14} textAnchor="middle" fontSize="11" fill="#6b7280">total</text>
      </svg>
      <div className="flex flex-col gap-2">
        {entries.map(([status, count]) => (
          <div key={status} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: statusColors[status] || '#94a3b8' }} />
            <span className="text-sm text-gray-600 capitalize w-20">{status.replace('_', ' ')}</span>
            <span className="text-sm font-bold text-gray-900">{count}</span>
            <span className="text-xs text-gray-400">({Math.round(count / total * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};


// ================================================================
// MAIN PAGE
// ================================================================
const AdminAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') navigate('/admin/login');
  }, [user, navigate]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/admin/platform/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data.analytics);
      } catch (err) { console.error('Analytics error:', err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="flex items-center justify-center py-32"><LoadingSpinner size="lg" /></div>
    <Footer /></div>
  );
  if (!data) return null;

  const o = data.overview;

  const Growth = ({ value }) => {
    const n = parseFloat(value);
    if (!n) return <span className="text-xs text-gray-400 flex items-center gap-0.5"><Minus className="w-3 h-3" /> 0%</span>;
    const up = n > 0;
    return (
      <span className={`flex items-center gap-0.5 text-xs font-semibold ${up ? 'text-green-600' : 'text-red-500'}`}>
        {up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
        {Math.abs(n).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-500" /> Platform Analytics
            </h1>
            <p className="text-sm text-gray-500 mt-1">Platform-wide performance metrics and trends</p>
          </div>

          {/* ─── KPI Cards ─── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Total Users', val: o.totalUsers, sub: `+${o.newUsersThisMonth} this month`, Icon: Users, bg: 'bg-blue-50', ic: 'text-blue-500' },
              { label: 'Businesses', val: o.totalBusinesses, Icon: Users, bg: 'bg-purple-50', ic: 'text-purple-500' },
              { label: 'Wholesalers', val: o.totalWholesalers, Icon: Package, bg: 'bg-green-50', ic: 'text-green-500' },
              { label: 'Products', val: o.totalProducts, Icon: Package, bg: 'bg-amber-50', ic: 'text-amber-500' },
              { label: 'Total Orders', val: o.totalOrders, Icon: ShoppingCart, bg: 'bg-indigo-50', ic: 'text-indigo-500' },
            ].map(({ label, val, sub, Icon, bg, ic }, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">{label}</span>
                  <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${ic}`} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{val || 0}</p>
                {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
              </div>
            ))}
          </div>

          {/* ─── Revenue + Orders ─── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-gray-500">Revenue This Month</h3>
                <Growth value={o.revenueGrowth} />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">€{(o.revenueThisMonth || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-400">Last month: €{(o.revenueLastMonth || 0).toFixed(2)}</p>
              <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"
                  style={{ width: `${Math.min(((o.revenueThisMonth || 0) / Math.max(o.revenueLastMonth || 1, 1)) * 100, 100)}%` }} />
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-gray-500">Orders This Month</h3>
                <Growth value={o.orderGrowth} />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">{o.ordersThisMonth || 0}</p>
              <p className="text-xs text-gray-400">Last month: {o.ordersLastMonth || 0}</p>
              <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full"
                  style={{ width: `${Math.min(((o.ordersThisMonth || 0) / Math.max(o.ordersLastMonth || 1, 1)) * 100, 100)}%` }} />
              </div>
            </div>
          </div>

          {/* ─── Charts Row ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Monthly Orders</h3>
              <BarChart data={data.trends?.orders} valueKey="count" color="#6366f1" />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Order Status</h3>
              <DonutChart data={data.ordersByStatus} />
            </div>
          </div>

          {/* ─── Revenue Trend ─── */}
          {data.trends?.revenue?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Revenue Trend (Delivered Orders)</h3>
              <AreaChart data={data.trends.revenue} color="#10b981" />
            </div>
          )}

          {/* ─── Top Performers ─── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Top Wholesalers */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Top Wholesalers</h3>
              {(data.topWholesalers || []).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No data yet</p>
              ) : (
                <div className="space-y-4">
                  {data.topWholesalers.slice(0, 5).map((w, i) => {
                    const max = data.topWholesalers[0]?.orderCount || 1;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                              i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-gray-200 text-gray-500'
                            }`}>{i + 1}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{w.companyName}</p>
                              <p className="text-xs text-gray-400 capitalize">{w.tier || 'starter'}</p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <p className="text-sm font-bold text-gray-900">{w.orderCount} orders</p>
                            <p className="text-xs text-gray-400">€{(w.revenue || 0).toFixed(0)}</p>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden ml-9">
                          <div className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full transition-all"
                            style={{ width: `${(w.orderCount / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Top Businesses */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Top Businesses</h3>
              {(data.topBusinesses || []).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No data yet</p>
              ) : (
                <div className="space-y-4">
                  {data.topBusinesses.slice(0, 5).map((b, i) => {
                    const max = data.topBusinesses[0]?.orderCount || 1;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                              i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-gray-200 text-gray-500'
                            }`}>{i + 1}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{b.businessName}</p>
                              <p className="text-xs text-gray-400 capitalize">{b.businessType?.replace('_', ' ') || ''}</p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <p className="text-sm font-bold text-gray-900">{b.orderCount} orders</p>
                            <p className="text-xs text-gray-400">€{(b.spent || 0).toFixed(0)}</p>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden ml-9">
                          <div className="h-full bg-gradient-to-r from-emerald-400 to-green-500 rounded-full transition-all"
                            style={{ width: `${(b.orderCount / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        </main>
      </div>
      <Footer />
    </div>
  );
};

export default AdminAnalytics;